import { Typography, Stack } from "@mui/material";
import { toSpanishDate } from "../../../helpers/dateFormat";
import { numberFormat } from "../../../helpers/numberFormat";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useCallback } from "react";
import {
  columnsLPByStartup,
  totals,
} from "../../helpers/columns/columnsPosition";
import { TableLPWithTotals } from "../../layout/TableLPWithTotals";
import { useTranslation } from "react-i18next";
import { createAllInvestmentLpByStartup } from "../../../helpers/exportToExcel";

export const LPByStartup = ({ distributionList, percentage, generalSheet }) => {
  const { t } = useTranslation("global");

  const columns = [
    {
      field: "investmentDate",
      headerName: t("lptable.investmentDate"),
      minWidth: 140,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography color="primary" variant="body2">
          {toSpanishDate(params.row.investmentDate)}
        </Typography>
      ),
    },
    {
      field: "roundNumber",
      headerName: t("lptable.round"),
      minWidth: 80,
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "investmentType",
      headerName: t("lptable.investmentType"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) =>
        row.investmentType === "Ampliación Capital"
          ? t("opportunities.investmentTypeCapitalIncrease")
          : t("opportunities.investmentTypeEquityLoan"),
    },
    {
      field: "amount",
      headerName: t("lptable.fundCost"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => numberFormat(row.amount),
    },
    {
      field: "fundValue",
      headerName: t("lptable.fundValue"),
      minWidth: 180,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.fundValue) {
          return numberFormat(row.fundValue.toFixed(2));
        } else {
          const shares = row.amount / row.participationValuation;
          const value = (row.participationValuation && row.lastValoration !== null && row.lastValoration !== undefined)
            ? row.lastValoration * shares
            : row.amount;
          return numberFormat(value.toFixed(2));
        }
      },
    },
    {
      field: "LPCost",
      headerName: t("lptable.LPCost"),
      minWidth: 120,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        return numberFormat((row.amount * (percentage / 100)).toFixed(2));
      },
    },
    {
      field: "LPValue",
      headerName: t("lptable.LPValue"),
      minWidth: 160,
      flex: 1,
      align: "center",
      headerAlign: "center",
      valueGetter: ({ row }) => {
        if (row.LPValue) {
          return numberFormat(row.LPValue.toFixed(2));
        } else {
          const shares =
            (row.amount * (percentage / 100)) / row.participationValuation;
          const value = (row.participationValuation && row.lastValoration !== null && row.lastValoration !== undefined)
            ? row.lastValoration * shares
            : row.amount * (percentage / 100);
          return numberFormat(value.toFixed(2));
        }
      },
    },
  ];
  const distributionListByStartup = distributionList.reduce((acc, obj) => {
    const alias = obj?.alias;
    const existingStartup = acc.find((s) => s.startup === alias);
    if (existingStartup) {
      existingStartup.data.push(obj);
    } else {
      acc.push({ startup: alias, data: [obj], id: alias, url: obj?.url });
    }
    return acc;
  }, []);
  const totalsArray = totals(distributionListByStartup, percentage); // return array whit all startup sum
  const totalData = [
    {
      amount: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.amount) || 0), 0).toFixed(2)
      ),
      fundValue: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.fundValue) || 0), 0).toFixed(2)
      ),
      LPCost: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.LPCost) || 0), 0).toFixed(2)
      ),
      LPValue: numberFormat(
        totalsArray.reduce((acc, row) => acc + (Number(row.LPValue) || 0), 0).toFixed(2)
      ),
      startup: "Totales Latentes",
      url: "",
      id: "Totales Latentes",
    },
  ]; // totals rows

  const results = totalData.concat(distributionListByStartup); // add totals rows in distributionListByStartup
  const ViewDetails = useCallback(
    (investment) => {
      return (
        <Stack>
          <DataGridPro
            density="compact"
            autoHeight
            columns={columns}
            rows={investment.data}
            sx={{ flex: 1 }}
            hideFooter
          />
        </Stack>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const startupSheet = createAllInvestmentLpByStartup(
    columnsLPByStartup(t, percentage).map((col) => col.headerName),
    results,
    generalSheet?.columns[2],
    percentage
  );

  const generalStartupSheet = [generalSheet, startupSheet];

  return (
    <>
      <TableLPWithTotals
        rows={results}
        columns={columnsLPByStartup(t, percentage)}
        title="positionLP"
        detail={ViewDetails}
        generalStartupSheet={generalStartupSheet}
        percentage={percentage}
        columnsToExcel={columns.map((col) => col.headerName)}
      />
    </>
  );
};
